import { useCallback, useEffect, useRef, type KeyboardEvent } from "react";

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * Modal plumbing for any dialog: while `open`, it remembers the opener, locks page scroll and moves
 * focus in (to `initialFocusId` when it names an element inside the dialog, else the title); it
 * keeps Tab inside the dialog and routes Escape to `onClose`; on close it gives focus back to the
 * opener. Pair it with `DialogShell` (spread `dialogRef`, `titleRef`, `onKeyDown`).
 */
export function useModalDialog(open: boolean, onClose: () => void, initialFocusId?: string) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  /** Moves focus to an element in the dialog by id, else to the title. */
  const focusInside = useCallback((id?: string) => {
    const found = id ? document.getElementById(id) : null;
    const target = found && dialogRef.current?.contains(found) ? found : titleRef.current;
    target?.focus();
  }, []);

  /** Puts focus on the title again, e.g. when the dialog moves to its next screen. */
  const focusTitle = useCallback(() => titleRef.current?.focus(), []);

  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    focusInside(initialFocusId);
    return () => {
      document.body.style.overflow = overflow;
      if (opener?.isConnected) opener.focus();
    };
    // Focus moves in once per opening; the target id is read at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      onCloseRef.current();
      return;
    }
    if (event.key !== "Tab" || !dialogRef.current) return;
    const focusables = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === titleRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }, []);

  return { dialogRef, titleRef, onKeyDown, focusTitle, focusInside };
}

export default useModalDialog;
